import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Button, Badge } from 'react-bootstrap';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Helmet } from 'react-helmet';
import FAQSection from './FAQSection';

const NetSuiteSupportPage = () => {
  const [activeProcessStep, setActiveProcessStep] = useState(0);
  const [hoveredService, setHoveredService] = useState(null);
  const controls = useAnimation();
  const [inView] = useInView();

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 50 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.3
      }
    }
  };



  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  // Auto-rotate process steps
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveProcessStep(prev => (prev < 3 ? prev + 1 : 0));
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const supportServices = [
    {
      title: "24/7 Technical Support",
      description: "Round-the-clock NetSuite assistance for urgent issues, performance bottlenecks, and unexpected system failures.",
      icon: "bi-headset",
      color: "#4fc3f7"
    },
    {
      title: "Certified NetSuite Experts",
      description: "Our support team consists of certified NetSuite consultants, developers, and administrators with extensive hands-on experience.",
      icon: "bi-award",
      color: "#66bb6a"
    },
    {
      title: "Custom Support Plans",
      description: "Tailored solutions based on your company size, application complexity, and business objectives.",
      icon: "bi-sliders",
      color: "#ffa726"
    },
    {
      title: "User Training & Enablement",
      description: "Empower your team with personalized training sessions and practical guides to maximize user adoption.",
      icon: "bi-book",
      color: "#ec407a"
    },
    {
      title: "Proactive System Monitoring",
      description: "Our team performs regular system audits and health checks to prevent issues before they occur.",
      icon: "bi-graph-up",
      color: "#ab47bc"
    }
  ];

  const technicalServices = [
    {
      title: "System Troubleshooting",
      description: "Rapid diagnosis and resolution of system errors, bugs, and performance issues.",
      icon: "bi-tools",
      progress: 90,
      color: "#FF6B6B",
      bgGradient: "linear-gradient(135deg, #FF6B6B, #FF5252)",
      shadowColor: "rgba(255, 107, 107, 0.4)"
    },
    {
      title: "Data Management & Security",
      description: "Expert assistance with data migration, backup strategies, and security best practices.",
      icon: "bi-shield-lock",
      progress: 85,
      color: "#4ECDC4",
      bgGradient: "linear-gradient(135deg, #4ECDC4, #26A69A)",
      shadowColor: "rgba(78, 205, 196, 0.4)"
    },
    {
      title: "Customization & Enhancement",
      description: "Modify and extend NetSuite functionalities to perfectly align with your business needs.",
      icon: "bi-gear",
      progress: 80,
      color: "#45B7D1",
      bgGradient: "linear-gradient(135deg, #45B7D1, #2196F3)",
      shadowColor: "rgba(69, 183, 209, 0.4)"
    },
    {
      title: "Performance Optimization",
      description: "Improve system speed, efficiency, and reporting capabilities for better productivity.",
      icon: "bi-speedometer2",
      progress: 95,
      color: "#96CEB4",
      bgGradient: "linear-gradient(135deg, #96CEB4, #66BB6A)",
      shadowColor: "rgba(150, 206, 180, 0.4)"
    },
    {
      title: "Integration Support",
      description: "Seamless integration with third-party applications and essential business tools.",
      icon: "bi-plug",
      progress: 75,
      color: "#FECA57",
      bgGradient: "linear-gradient(135deg, #FECA57, #FFC107)",
      shadowColor: "rgba(254, 202, 87, 0.4)"
    },
    {
      title: "Continuous Maintenance",
      description: "Keep your system updated with the latest NetSuite features and security patches.",
      icon: "bi-arrow-repeat",
      progress: 88,
      color: "#A8E6CF",
      bgGradient: "linear-gradient(135deg, #A8E6CF, #81C784)",
      shadowColor: "rgba(168, 230, 207, 0.4)"
    }
  ];

  return (
    <div style={{
      overflowX: 'hidden',
      fontFamily: "'Poppins', sans-serif",
      backgroundColor: '#f8f9fa'
    }}>
      <Helmet>
        <title>NetSuite Support Services USA | SpireNest Assistance</title>
        <link rel="canonical" href="https://spirenest.co/netsuite/services/support" />
        <meta name="description" content="Receive reliable NetSuite support from SpireNest. Our US-based team ensures your ERP system runs smoothly with prompt issue resolution." />
        <meta name="keywords" content="NetSuite support, ERP support, NetSuite assistance, USA, SpireNest" />
        <meta property="og:title" content="NetSuite Support Services USA | SpireNest" />
        <meta property="og:description" content="Get ongoing NetSuite support, troubleshooting, user training, and performance optimization." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://spirenest.co/netsuite-support" />
        <meta property="og:image" content="https://spirenest.co/assets/images/og-support.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="NetSuite Support Services USA | SpireNest" />
        <meta name="twitter:description" content="Reliable ERP support to maintain and scale your NetSuite environment." />
        <meta name="twitter:image" content="https://spirenest.co/assets/images/og-support.jpg" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "WebPage",
                "@id": "https://spirenest.co/services/support",
                "name": "SpireNest | NetSuite Support Services",
                "description": "Get reliable NetSuite support from SpireNest. Our team ensures minimal downtime and quick issue resolution to keep your ERP running smoothly.",
                "url": "https://spirenest.co/services/support",
                "image": "https://spirenest.co/assets/images/og-home.jpg",
                "publisher": {
                  "@type": "Organization",
                  "name": "SpireNest",
                  "url": "https://spirenest.co/",
                  "logo": {
                    "@type": "ImageObject",
                    "url": "https://spirenest.co/assets/logo-Cko-LaIl.png"
                  }
                }
              },
              {
                "@type": "BreadcrumbList",
                "@id": "https://spirenest.co/services/support#breadcrumb",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Home",
                    "item": "https://spirenest.co/"
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "name": "Services",
                    "item": "https://spirenest.co/services/"
                  },
                  {
                    "@type": "ListItem",
                    "position": 3,
                    "name": "Support",
                    "item": "https://spirenest.co/services/support"
                  }
                ]
              }
            ]
          })}
        </script>
      </Helmet>
      {/* Animated Hero Section with Floating Elements */}
      <section style={{
        background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
        color: 'white',
        padding: '8rem 0',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Floating tech elements */}
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0 }}
            animate={{
              opacity: [0, 0.6, 0],
              x: Math.random() * 1000 - 500,
              y: Math.random() * 1000 - 500,
            }}
            transition={{
              duration: Math.random() * 15 + 10,
              repeat: Infinity,
              ease: "linear",
              delay: Math.random() * 5
            }}
            style={{
              position: 'absolute',
              width: '3px',
              height: '3px',
              background: 'white',
              borderRadius: '50%'
            }}
          />
        ))}
        
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-5 mb-lg-0">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={staggerContainer}
              >
                <motion.div variants={fadeInUp}>
                  <Badge 
                    pill 
                    style={{ 
                      background: 'rgba(255,255,255,0.2)', 
                      color: 'white',
                      fontSize: '0.8rem',
                      padding: '8px 16px',
                      marginBottom: '1rem',
                      display: 'inline-block'
                    }}
                  >
                    NETSUITE SUPPORT SOLUTIONS
                  </Badge>
                </motion.div>
                
                <motion.h1 
                  className="display-3 fw-bold mb-4" 
                  variants={fadeInUp}
                  style={{ 
                    lineHeight: '1.2',
                    textShadow: '0 4px 20px rgba(0,0,0,0.3)'
                  }}
                >
                  Optimize & Maintain Your <span style={{ 
                    color: '#4fc3f7',
                    textShadow: '0 0 15px rgba(79, 195, 247, 0.7)'
                  }}>NetSuite ERP</span>
                </motion.h1>
                
                <motion.p 
                  className="lead mb-4" 
                  variants={fadeInUp}
                  style={{ 
                    fontSize: '1.25rem',
                    opacity: 0.9
                  }}
                >
                  Ensure your NetSuite ERP runs at peak performance with our expert support services
                </motion.p>
                
                <motion.div variants={fadeInUp} className="d-flex flex-wrap gap-3">
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Button 
                      variant="light" 
                      size="lg" 
                      className="rounded-pill px-4 py-3 fw-bold position-relative overflow-hidden"
                      style={{
                        background: 'white',
                        color: '#1a237e',
                        boxShadow: '0 8px 25px rgba(0,0,0,0.2)'
                      }}
                    >
                      <span style={{ position: 'relative', zIndex: 2 }}>
                      <a href="/contact" className="btn  btn-lg px-4 text-primary ">
                      Get Support Now
                </a>
                        
                      </span>
                      <motion.span
                        initial={{ x: '-100%', opacity: 0 }}
                        whileHover={{ x: '100%', opacity: 0.15 }}
                        transition={{ duration: 0.8 }}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          background: 'linear-gradient(90deg, transparent, rgba(79, 195, 247, 0.4), transparent)',
                          zIndex: 1
                        }}
                      />
                    </Button>
                  </motion.div>
                  
                  
                </motion.div>
              </motion.div>
            </Col>
            
            <Col lg={6}>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                style={{
                  position: 'relative',
                  height: '400px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                {/* Interactive Service Orbit */}
                <motion.div
                  animate={{
                    rotate: 360
                  }}
                  transition={{
                    duration: 40,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                  style={{
                    position: 'absolute',
                    width: '300px',
                    height: '300px',
                    borderRadius: '50%',
                    border: '2px dashed rgba(79, 195, 247, 0.3)'
                  }}
                >
                  {supportServices.map((service, index) => (
                    <motion.div
                      key={index}
                      whileHover={{ scale: 1.2 }}
                      style={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        width: '60px',
                        height: '60px',
                        marginLeft: '-30px',
                        marginTop: '-30px',
                        borderRadius: '50%',
                        background: service.color,
                        color: 'white',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        fontSize: '1.5rem',
                        cursor: 'pointer',
                        boxShadow: `0 5px 15px ${service.color}80`,
                        transform: `rotate(${index * 72}deg) translate(150px) rotate(-${index * 72}deg)`
                      }}
                      onMouseEnter={() => setHoveredService(index)}
                      onMouseLeave={() => setHoveredService(null)}
                    >
                      <i className={`bi ${service.icon}`}></i>
                    </motion.div>
                  ))}
                </motion.div>
                
                {/* Central Hub */}
                <motion.div
                  animate={{
                    scale: [1, 1.05, 1],
                    boxShadow: ['0 10px 30px rgba(0,0,0,0.2)', '0 15px 40px rgba(79, 195, 247, 0.3)', '0 10px 30px rgba(0,0,0,0.2)']
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  style={{
                    width: '180px',
                    height: '180px',
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.1)',
                    backdropFilter: 'blur(5px)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    color: 'white',
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    padding: '2rem',
                    zIndex: 2
                  }}
                >
                  NetSuite Support Hub
                </motion.div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Floating Service Card */}
      {hoveredService !== null && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          style={{
            position: 'absolute',
            top: '60%',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
            width: '100%',
            maxWidth: '500px',
            padding: '0 1rem'
          }}
        >
          <Card className="shadow-lg" style={{
            border: 'none',
            borderRadius: '15px',
            background: 'white',
            borderTop: `4px solid ${supportServices[hoveredService].color}`
          }}>
            <Card.Body className="p-4">
              <div className="d-flex align-items-center mb-3">
                <div style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  background: supportServices[hoveredService].color,
                  color: 'white',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontSize: '1.5rem',
                  marginRight: '1rem'
                }}>
                  <i className={`bi ${supportServices[hoveredService].icon}`}></i>
                </div>
                <h4 className="mb-0" style={{ color: '#1a237e' }}>
                  {supportServices[hoveredService].title}
                </h4>
              </div>
              <p>{supportServices[hoveredService].description}</p>
            </Card.Body>
          </Card>
        </motion.div>
      )}

      {/* Technical Services Section with Animated Cards */}
      <section style={{ 
        padding: '6rem 0', 
        position: 'relative',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        overflow: 'hidden'
      }}>
        {/* Enhanced Decorative background elements */}
        <motion.div
          animate={{
            x: [0, 30, 0],
            y: [0, -25, 0],
            rotate: [0, 180, 360]
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          style={{
            position: 'absolute',
            top: '10%',
            left: '5%',
            width: '150px',
            height: '150px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(79, 195, 247, 0.15) 0%, transparent 70%)',
            filter: 'blur(40px)',
            zIndex: 0
          }}
        />
        
        <motion.div
          animate={{
            x: [0, -25, 0],
            y: [0, 20, 0],
            rotate: [360, 180, 0]
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 5
          }}
          style={{
            position: 'absolute',
            bottom: '15%',
            right: '8%',
            width: '200px',
            height: '200px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255, 107, 107, 0.12) 0%, transparent 70%)',
            filter: 'blur(50px)',
            zIndex: 0
          }}
        />
        
        <Container style={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-5"
          >
            <motion.div variants={fadeInUp}>
              <motion.span
                style={{
                  display: 'inline-block',
                  background: 'linear-gradient(90deg, #1a237e, #4fc3f7)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  color: 'transparent',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  letterSpacing: '1px',
                  marginBottom: '1rem',
                  textTransform: 'uppercase'
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                Technical Services
              </motion.span>
              <h2 className="fw-bold mb-3" style={{ 
                color: '#1a237e',
                fontSize: '2.8rem',
                lineHeight: 1.2
              }}>
                Comprehensive <span style={{ 
                  background: 'linear-gradient(90deg, #4fc3f7, #FF6B6B)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  color: 'transparent'
                }}>NetSuite Support</span> Services
              </h2>
            </motion.div>
            
            <motion.div variants={fadeInUp}>
              <p className="lead" style={{ 
                color: '#5c6bc0',
                maxWidth: '800px',
                margin: '0 auto',
                fontSize: '1.3rem',
                lineHeight: 1.6
              }}>
                From troubleshooting to optimization, we provide end-to-end support for your NetSuite ecosystem with cutting-edge solutions
              </p>
            </motion.div>
          </motion.div>
          
          <Row className="g-4">
            {technicalServices.map((service, index) => (
              <Col md={6} lg={4} key={index}>
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                  transition={{ delay: index * 0.15 }}
                >
                  <motion.div
                    whileHover={{ 
                      y: -15,
                      rotateY: 5,
                      scale: 1.02
                    }}
                    whileTap={{ scale: 0.98 }}
                    transition={{ 
                      type: "spring", 
                      stiffness: 300,
                      damping: 20
                    }}
                    style={{ height: '100%' }}
                  >
                    <Card 
                      className="border-0 rounded-4 overflow-hidden position-relative"
                      style={{
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                        cursor: 'pointer',
                        overflow: 'hidden',
                        border: '1px solid rgba(0,0,0,0.05)',
                        height: '250px',
                        minHeight: '250px',
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      {/* Gradient overlay on hover */}
                      <motion.div
                        className="position-absolute w-100 h-100"
                        initial={{ opacity: 0 }}
                        whileHover={{ opacity: 1 }}
                        style={{
                          background: `linear-gradient(135deg, ${service.color}08, ${service.color}15)`,
                          zIndex: 1
                        }}
                      />
                      
                      {/* Animated accent border */}
                      <motion.div
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '4px',
                          background: service.bgGradient,
                          zIndex: 2
                        }}
                        initial={{ scaleX: 0, originX: 0 }}
                        whileInView={{ scaleX: 1 }}
                        transition={{ duration: 0.8, delay: index * 0.1 }}
                        viewport={{ once: true }}
                      />
                      
                      <Card.Body className="p-4 position-relative d-flex flex-column" style={{ zIndex: 2, flex: '1' }}>
                        <div className="d-flex align-items-center mb-3">
                          {/* Enhanced colorful icon */}
                          <motion.div
                            style={{
                              width: '70px',
                              height: '70px',
                              borderRadius: '20px',
                              background: service.bgGradient,
                              color: 'white',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              fontSize: '2rem',
                              marginRight: '1rem',
                              position: 'relative',
                              overflow: 'hidden'
                            }}
                            whileHover={{ 
                              rotate: [0, -10, 10, 0],
                              scale: 1.1
                            }}
                            transition={{ duration: 0.5 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <motion.div
                              initial={{ rotate: 0 }}
                              whileHover={{ rotate: 360 }}
                              transition={{ duration: 0.8 }}
                            >
                              <i className={`bi ${service.icon}`}></i>
                            </motion.div>
                            
                            {/* Shimmer effect */}
                            <motion.div
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: '-100%',
                                width: '100%',
                                height: '100%',
                                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)'
                              }}
                              whileHover={{
                                left: '100%'
                              }}
                              transition={{ duration: 0.6 }}
                            />
                          </motion.div>
                          
                          <div>
                            <h4 className="mb-0" style={{ 
                              color: '#1a237e',
                              fontSize: '1.25rem',
                              fontWeight: 'bold'
                            }}>
                              {service.title}
                            </h4>
                          </div>
                        </div>
                        
                        <p className="text-muted" style={{ 
                          fontSize: '1rem',
                          lineHeight: 1.6,
                          flex: '1',
                          marginBottom: '2rem'
                        }}>
                          {service.description}
                        </p>
                        
                        {/* Enhanced progress bar */}
                        
                        
                        {/* Floating particles effect on hover */}
                        <motion.div
                          className="position-absolute"
                          style={{
                            top: '20px',
                            right: '20px',
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            background: `${service.color}15`,
                            zIndex: 0
                          }}
                          whileHover={{
                            scale: [1, 1.5, 1],
                            opacity: [0.3, 0.6, 0.3]
                          }}
                          transition={{
                            duration: 1.5,
                            repeat: Infinity
                          }}
                        />
                      </Card.Body>
                      
                      {/* Enhanced bottom accent */}
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: '100%' }}
                        transition={{ duration: 1, delay: index * 0.1 + 0.3 }}
                        viewport={{ once: true }}
                        whileHover={{
                          boxShadow: `0 0 20px ${service.shadowColor}`
                        }}
                        style={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          height: '4px',
                          background: service.bgGradient,
                          zIndex: 2
                        }}
                      />
                      
                      {/* Card shadow enhancement on hover */}
                      <motion.div
                        className="position-absolute w-100 h-100"
                        style={{
                          top: 0,
                          left: 0,
                          borderRadius: '1rem',
                          zIndex: -1
                        }}
                        whileHover={{
                          boxShadow: `0 20px 40px ${service.shadowColor}`
                        }}
                        transition={{ duration: 0.3 }}
                      />
                    </Card>
                  </motion.div>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Interactive Process Section */}
      <section style={{ 
        padding: '6rem 0',
        background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Tech circles background */}
        <motion.div
          animate={{
            rotate: 360
          }}
          transition={{
            duration: 60,
            repeat: Infinity,
            ease: "linear"
          }}
          style={{
            position: 'absolute',
            top: '20%',
            left: '10%',
            width: '100px',
            height: '100px',
            background: 'url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCI+PHBhdGggZD0iTTEyLDIwQTgsOCAwIDAsMSA0LDEyQTgsOCAwIDAsMSAxMiw0QTgsOCAwIDAsMSAyMCwxMkE4LDggMCAwLDEgMTIsMjBNMTIsMkExMCwxMCAwIDAsMCAyLDEyQTEwLDEwIDAgMCwwIDEyLDIyQTEwLDEwIDAgMCwwIDIyLDEyQTEwLDEwIDAgMCwwIDEyLDJaIiBmaWxsPSJ3aGl0ZSIgZmlsbC1vcGFjaXR5PSIwLjEiLz48L3N2Zz4=")',
            opacity: 0.3
          }}
        />
        
        <Container style={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-5"
          >
            <motion.div variants={fadeInUp}>
              <Badge pill bg="primary" className="mb-3" style={{ 
                fontSize: '0.8rem',
                padding: '0.5rem 1rem',
                background: 'rgba(25, 118, 210, 0.2)',
                color: '#1a237e',
                fontWeight: '500'
              }}>
                OUR PROCESS
              </Badge>
            </motion.div>
            
            <motion.div variants={fadeInUp}>
              <h2 className="fw-bold mb-3" style={{ 
                color: '#1a237e',
                fontSize: '2.5rem'
              }}>
                How We Deliver <span style={{ color: '#4fc3f7' }}>Exceptional Support</span>
              </h2>
            </motion.div>
          </motion.div>
          
          <Row className="align-items-center">
            <Col lg={6} className="mb-5 mb-lg-0">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8 }}
                style={{
                  position: 'relative',
                  height: '400px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                {/* Animated Process Visualization */}
                <motion.div
                  animate={{
                    rotate: 360
                  }}
                  transition={{
                    duration: 30,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                  style={{
                    position: 'absolute',
                    width: '300px',
                    height: '300px',
                    borderRadius: '50%',
                    border: '15px solid rgba(79, 195, 247, 0.2)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <motion.div
                    animate={{
                      rotate: -360
                    }}
                    transition={{
                      duration: 25,
                      repeat: Infinity,
                      ease: "linear"
                    }}
                    style={{
                      position: 'absolute',
                      width: '250px',
                      height: '250px',
                      borderRadius: '50%',
                      border: '15px solid rgba(76, 175, 80, 0.2)',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                  >
                    <motion.div
                      animate={{
                        rotate: 360
                      }}
                      transition={{
                        duration: 20,
                        repeat: Infinity,
                        ease: "linear"
                      }}
                      style={{
                        position: 'absolute',
                        width: '200px',
                        height: '200px',
                        borderRadius: '50%',
                        border: '15px solid rgba(255, 193, 7, 0.2)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}
                    >
                      <motion.div
                        style={{
                          width: '150px',
                          height: '150px',
                          borderRadius: '50%',
                          background: 'white',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                          fontSize: '1.8rem',
                          fontWeight: 'bold',
                          color: '#1a237e'
                        }}
                        animate={{
                          scale: [1, 1.05, 1]
                        }}
                        transition={{
                          duration: 3,
                          repeat: Infinity,
                          ease: "easeInOut"
                        }}
                      >
                        Step {activeProcessStep + 1}
                      </motion.div>
                    </motion.div>
                  </motion.div>
                </motion.div>
                
                {/* Process Steps Indicators */}
                {/* {[1, 2, 3, 4].map((step, index) => (
                  <motion.div 
                    key={index}
                    animate={{
                      background: activeProcessStep >= index ? [
                        '#4fc3f7', '#66bb6a', '#ffa726', '#ec407a'
                      ][index] : '#e0e0e0',
                      scale: activeProcessStep === index ? [1, 1.15, 1] : 1,
                      boxShadow: activeProcessStep === index ? 
                        `0 10px 25px ${['rgba(79, 195, 247, 0.4)', 'rgba(102, 187, 106, 0.4)', 'rgba(255, 167, 38, 0.4)', 'rgba(236, 64, 122, 0.4)'][index]}` :
                        '0 5px 15px rgba(0,0,0,0.2)'
                    }}
                    transition={{
                      background: { duration: 0.5 },
                      scale: { duration: 2, repeat: activeProcessStep === index ? Infinity : 0 },
                      boxShadow: { duration: 0.5 }
                    }}
                    style={{
                      position: 'absolute',
                      width: '60px',
                      height: '60px',
                      borderRadius: '50%',
                      color: 'white',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      fontWeight: 'bold',
                      transform: `rotate(${index * 90}deg) translate(180px) rotate(-${index * 90}deg)`,
                      zIndex: 5,
                      cursor: 'pointer',
                      border: activeProcessStep === index ? '3px solid white' : '2px solid rgba(255,255,255,0.3)'
                    }}
                    onClick={() => setActiveProcessStep(index)}
                    whileHover={{ 
                      scale: 1.2,
                      transition: { duration: 0.2 }
                    }}
                    whileTap={{ scale: 0.9 }}
                  >
                    {step}
                  </motion.div>
                ))} */}
              </motion.div>
            </Col>
            
            <Col lg={6}>
              <div className="process-steps">
                {[
                  {
                    step: 1,
                    title: "Comprehensive Assessment",
                    content: "We conduct a thorough evaluation of your system's current performance and identify key areas for improvement.",
                    icon: "bi-clipboard-data",
                    color: "#4fc3f7"
                  },
                  {
                    step: 2,
                    title: "Precision Issue Resolution",
                    content: "Our certified experts diagnose and resolve existing problems while optimizing your NetSuite configuration.",
                    icon: "bi-tools",
                    color: "#66bb6a"
                  },
                  {
                    step: 3,
                    title: "Proactive System Monitoring",
                    content: "We implement continuous monitoring solutions to ensure optimal performance and prevent future issues.",
                    icon: "bi-graph-up",
                    color: "#ffa726"
                  },
                  {
                    step: 4,
                    title: "User Training & Enablement",
                    content: "We empower your team with customized training programs to maximize system utilization and ROI.",
                    icon: "bi-people",
                    color: "#ec407a"
                  }
                ].map((step, index) => (
                  <motion.div 
                    key={index}
                    className={`process-step ${activeProcessStep === index ? 'active' : ''}`}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ 
                      opacity: activeProcessStep === index ? 1 : 0.6,
                      x: 0,
                      scale: activeProcessStep === index ? 1 : 0.95
                    }}
                    transition={{ duration: 0.5 }}
                    whileHover={activeProcessStep !== index ? { opacity: 0.8, scale: 0.98 } : {}}
                    onClick={() => setActiveProcessStep(index)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="step-header">
                      <motion.div 
                        className="step-number"
                        style={{ 
                          background: activeProcessStep === index ? step.color : '#e0e0e0',
                          color: activeProcessStep === index ? 'white' : '#666'
                        }}
                        animate={{
                          background: activeProcessStep === index ? step.color : '#e0e0e0',
                          color: activeProcessStep === index ? 'white' : '#666',
                          scale: activeProcessStep === index ? [1, 1.1, 1] : 1
                        }}
                        transition={{
                          background: { duration: 0.3 },
                          color: { duration: 0.3 },
                          scale: { duration: 1.5, repeat: activeProcessStep === index ? Infinity : 0 }
                        }}
                      >
                        {step.step}
                      </motion.div>
                      <h4 style={{ 
                        color: activeProcessStep === index ? '#1a237e' : '#666',
                        transition: 'color 0.3s ease'
                      }}>
                        {step.title}
                      </h4>
                    </div>
                    
                    <motion.p 
                      className="step-content"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ 
                        opacity: activeProcessStep === index ? 1 : 0,
                        height: activeProcessStep === index ? 'auto' : 0
                      }}
                      transition={{ 
                        opacity: { duration: 0.3, delay: activeProcessStep === index ? 0.2 : 0 },
                        height: { duration: 0.4 }
                      }}
                      style={{ 
                        overflow: 'hidden',
                        paddingTop: activeProcessStep === index ? '0.5rem' : '0'
                      }}
                    >
                      {step.content}
                    </motion.p>
                  </motion.div>
                ))}
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Final CTA with Floating Elements */}
      <section style={{ 
        padding: '6rem 0',
        background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
        color: 'white',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Floating elements */}
        <motion.div
          animate={{
            y: [0, -30, 0],
            x: [0, 20, 0]
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          style={{
            position: 'absolute',
            top: '20%',
            left: '15%',
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(79, 195, 247, 0.2) 0%, transparent 70%)',
            zIndex: 0
          }}
        />
        
        <motion.div
          animate={{
            y: [0, 30, 0],
            x: [0, -20, 0]
          }}
          transition={{
            duration: 18,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
          style={{
            position: 'absolute',
            bottom: '20%',
            right: '15%',
            width: '120px',
            height: '120px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(102, 187, 106, 0.2) 0%, transparent 70%)',
            zIndex: 0
          }}
        />
        
        <Container style={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp}>
              <h2 className="display-5 fw-bold mb-4">
                Ready to Optimize Your NetSuite Experience?
              </h2>
            </motion.div>
            
            <motion.div variants={fadeInUp}>
              <p className="lead mb-5" style={{ fontSize: '1.25rem', opacity: 0.9 }}>
                Our expert team is standing by to help you maximize your NetSuite investment
              </p>
            </motion.div>
            
            <motion.div variants={fadeInUp}>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button 
                  variant="light" 
                  size="lg" 
                  className="rounded-pill px-4 py-3 fw-bold position-relative overflow-hidden"
                  style={{
                    background: 'white',
                    color: '#1a237e',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.2)'
                  }}
                >
                  <span style={{ position: 'relative', zIndex: 2 }}>
                  <a href="/contact" className='btn'>
                  Get Expert Support Now
                </a>
                    
                  </span>
                  <motion.span
                    initial={{ x: '-100%', opacity: 0 }}
                    whileHover={{ x: '100%', opacity: 0.15 }}
                    transition={{ duration: 0.8 }}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(79, 195, 247, 0.4), transparent)',
                      zIndex: 1
                    }}
                  />
                </Button>
              </motion.div>
            </motion.div>
          </motion.div>
        </Container>
      </section>

      {/* FAQ Section */}
      <FAQSection />

      {/* Global Styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');
        
        /* Process steps styling */
        .process-steps {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }
        
        .process-step {
          background: white;
          border-radius: 12px;
          padding: 1.5rem;
          box-shadow: 0 5px 15px rgba(0,0,0,0.05);
          transition: all 0.3s ease;
          border-left: 4px solid transparent;
        }
        
        .process-step.active {
          border-left-color: #4fc3f7;
          box-shadow: 0 10px 25px rgba(0,0,0,0.1);
        }
        
        .step-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1rem;
        }
        
        .step-number {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          color: white;
          display: flex;
          justify-content: center;
          align-items: center;
          font-weight: bold;
          flex-shrink: 0;
        }
        
        .step-content {
          color: #5c6bc0;
          margin-bottom: 0;
        }
        
        /* Technical Services Cards - Uniform Height */
        .technical-services-card {
          height: 350px !important;
          min-height: 350px !important;
        }
        
        /* Responsive adjustments */
        @media (max-width: 992px) {
          .process-step {
            padding: 1.25rem;
          }
          
          .step-header {
            gap: 0.75rem;
          }
          
          .technical-services-card {
            height: 320px !important;
            min-height: 320px !important;
          }
        }
        
        @media (max-width: 768px) {
          .process-steps {
            margin-top: 3rem;
          }
          
          .technical-services-card {
            height: 300px !important;
            min-height: 300px !important;
          }
        }
        
        @media (max-width: 576px) {
          .display-3 {
            font-size: 2.5rem;
          }
          
          .process-step {
            padding: 1rem;
          }
          
          .step-number {
            width: 35px;
            height: 35px;
            font-size: 0.9rem;
          }
          
          .technical-services-card {
            height: 280px !important;
            min-height: 280px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default NetSuiteSupportPage;